import React, { useState, useEffect } from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { FileUpload } from "primereact/fileupload";
import { Dialog } from "primereact/dialog";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";

import { useAuth } from "context/AuthContext";
import { useLoading } from "context/LoadingContext";
import { useNotification } from "context/NotificationContext";

import Api from "utils/Api";
import MaskUtil from "utils/MaskUtil";
import InputDate from "components/RH/inputs/InputDate";

const Desligamento = ({ id, funcionario }) => {
  const { setLoading } = useLoading();
  const auth = useAuth();
  const notify = useNotification();
  const Requicicao = new Api();

  // Estados para as opções dinâmicas
  const [motivosDesligamento, setMotivosDesligamento] = useState([]);
  const [tiposAvisoPrevio, setTiposAvisoPrevio] = useState([]);
  const [tiposDocumento, setTiposDocumento] = useState([]);

  const [Dados, setDados] = useState({
    dataDesligamento: "",
    motivoDesligamento: "",
    tipoAvisoPrevio: "",
    dataAvisoPrevio: "",
    dataUltimoDia: "",
    observacoes: "",
    documentos: []
  });

  const [Arquivo, setArquivo] = useState(null);
  const [TipoDocumento, setTipoDocumento] = useState(null);
  const [Historico, setHistorico] = useState([]);
  
  // Estados para controle de edição
  const [editandoDesligamento, setEditandoDesligamento] = useState(false);
  const [desligamentoAtivo, setDesligamentoAtivo] = useState(null);
  const [modoEdicao, setModoEdicao] = useState(false);
  
  // Estado para modal de confirmação de cancelamento
  const [showCancelarModal, setShowCancelarModal] = useState(false);
  
  // Estado para controlar carregamento inicial
  const [dadosInicializados, setDadosInicializados] = useState(false);

  // Carregar opções dinâmicas
  const CarregarOpcoes = async () => {
    try {
      setLoading(true);
      
      // Carregar motivos de desligamento
      const motivosResponse = await Requicicao.Get({
        endpoint: "/Funcionarios/Desligamento/motivos-desligamento",
        config: auth.GetHeaders()
      });

      if (motivosResponse?.data?.objetoResposta) {
        const motivosFormatados = motivosResponse.data.objetoResposta.map(item => ({
          label: item.nome,
          value: item.id
        }));
        setMotivosDesligamento(motivosFormatados);
      } else if (motivosResponse?.objetoResposta) {
        // Tentar acessar diretamente se não estiver em .data
        const motivosFormatados = motivosResponse.objetoResposta.map(item => ({
          label: item.nome,
          value: item.id
        }));
        setMotivosDesligamento(motivosFormatados);
      }

      // Carregar tipos de aviso prévio
      const avisoResponse = await Requicicao.Get({
        endpoint: "/Funcionarios/Desligamento/tipos-aviso-previo",
        config: auth.GetHeaders()
      });

      if (avisoResponse?.data?.objetoResposta) {
        const avisosFormatados = avisoResponse.data.objetoResposta.map(item => ({
          label: item.nome,
          value: item.id
        }));
        setTiposAvisoPrevio(avisosFormatados);
      } else if (avisoResponse?.objetoResposta) {
        // Tentar acessar diretamente se não estiver em .data
        const avisosFormatados = avisoResponse.objetoResposta.map(item => ({
          label: item.nome,
          value: item.id
        }));
        setTiposAvisoPrevio(avisosFormatados);
      }

      // Carregar tipos de documento
      const documentosResponse = await Requicicao.Get({
        endpoint: "/Funcionarios/Desligamento/tipos-documento",
        config: auth.GetHeaders()
      });

      if (documentosResponse?.data?.objetoResposta) {
        const documentosFormatados = documentosResponse.data.objetoResposta.map(item => ({
          label: item.nome,
          value: item.id
        }));
        setTiposDocumento(documentosFormatados);
      } else if (documentosResponse?.objetoResposta) {
        // Tentar acessar diretamente se não estiver em .data
        const documentosFormatados = documentosResponse.objetoResposta.map(item => ({
          label: item.nome,
          value: item.id
        }));
        setTiposDocumento(documentosFormatados);
      }

    } catch (error) {
      console.error("Erro ao carregar opções:", error);
      notify({ type: "error", message: "Erro ao carregar opções do formulário" });
      
      // Não usar fallback hardcoded - deixar os arrays vazios
      // Os dropdowns ficarão vazios se a API falhar
    } finally {
      setLoading(false);
    }
  };

  const HandleDropdownChange = (e, name) => {
    setDados((prevState) => ({
      ...prevState,
      [name]: e.value
    }));
  };

  const HandleInputChange = (e) => {
    const { name, value } = e.target;
    setDados((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  const HandleFileSelect = (e) => {
    setArquivo(e.files[0]);
  };

  const HandleDownloadDocumento = async (documentoId, nomeArquivo) => {
    if (!documentoId) {
      notify({ type: "error", message: "ID do documento não encontrado" });
      return;
    }

    try {
      setLoading(true);
      
      const response = await Requicicao.Get({
        endpoint: `/Funcionarios/Desligamento/Documentos/${documentoId}/Download`,
        config: {
          ...auth.GetHeaders("only-authentication"),
          responseType: "blob"
        }
      });

      // Criar blob e fazer download
      const blob = new Blob([response], { type: "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", nomeArquivo);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      notify({ type: "success", message: "Documento baixado com sucesso!" });
    } catch (error) {
      console.error("❌ [HandleDownloadDocumento] Erro:", error);
      
      if (error.response?.status === 404) {
        notify({ type: "error", message: "Documento não encontrado" });
      } else {
        notify({ type: "error", message: "Erro ao baixar documento" });
      }
    } finally {
      setLoading(false);
    }
  };

  const CarregarDadosParaEdicao = () => {
    if (desligamentoAtivo && dadosInicializados) {
      setDados({
        dataDesligamento: MaskUtil.applyDataMask(desligamentoAtivo.dataDesligamento),
        motivoDesligamento: desligamentoAtivo.motivoDesligamento,
        tipoAvisoPrevio: desligamentoAtivo.tipoAvisoPrevio,
        dataAvisoPrevio: MaskUtil.applyDataMask(desligamentoAtivo.dataAvisoPrevio),
        dataUltimoDia: MaskUtil.applyDataMask(desligamentoAtivo.dataUltimoDia),
        observacoes: desligamentoAtivo.observacoes || "",
        documentos: desligamentoAtivo.documentos || []
      });
      
      // Se há documentos, definir o tipo do primeiro documento
      if (desligamentoAtivo.documentos && desligamentoAtivo.documentos.length > 0) {
        setTipoDocumento(desligamentoAtivo.documentos[0].tipo);
      }
    }
  };

  const HabilitarEdicao = () => {
    setModoEdicao(true);
    setEditandoDesligamento(true);
  };

  const CancelarEdicao = () => {
    setModoEdicao(false);
    setEditandoDesligamento(false);
    // Recarregar dados originais
    CarregarDadosParaEdicao();
  };

  const LimparFormulario = () => {
    setEditandoDesligamento(false);
    setDesligamentoAtivo(null);
    setModoEdicao(false);
    setDados({
      dataDesligamento: "",
      motivoDesligamento: "",
      tipoAvisoPrevio: "",
      dataAvisoPrevio: "",
      dataUltimoDia: "",
      observacoes: "",
      documentos: []
    });
    setArquivo(null);
    setTipoDocumento(null);
  };

  const GetHistorico = async () => {
    try {
      // Se temos os dados do funcionário via props, usar diretamente
      if (funcionario) {
        const { desligamento } = funcionario;
        
        if (desligamento) {
          // Se há desligamento, criar um array com ele para manter compatibilidade
          const historico = [desligamento];
          setHistorico(historico);
          
          // Verificar se há desligamento ativo
          if (desligamento.status === "ativo") {
            setDesligamentoAtivo(desligamento);
            setEditandoDesligamento(true);
            // CarregarDadosParaEdicao será chamado pelo useEffect quando dadosInicializados = true
          } else {
            setDesligamentoAtivo(null);
            setEditandoDesligamento(false);
            LimparFormulario(); // Limpar dados se não houver desligamento ativo
          }
        } else {
          // Não há desligamento
          setHistorico([]);
          setDesligamentoAtivo(null);
          setEditandoDesligamento(false);
          LimparFormulario();
        }
      } else {
        // Fallback: fazer requisição se não tiver os dados via props
        const response = await Requicicao.Get({
          endpoint: `/Funcionarios/${id}`,
          config: auth.GetHeaders()
        });

        if (response.codigoEstadoHttp === 200) {
          const funcionarioData = response.objetoResposta;
          
          // Verificar se há desligamento no objeto do funcionário
          if (funcionarioData?.desligamento) {
            const { desligamento } = funcionarioData;
            
            // Se há desligamento, criar um array com ele para manter compatibilidade
            const historico = [desligamento];
            setHistorico(historico);
            
            // Verificar se há desligamento ativo
            if (desligamento.status === "ativo") {
              setDesligamentoAtivo(desligamento);
              setEditandoDesligamento(true); // Está editando um desligamento existente
              setModoEdicao(false); // Mas não está em modo de edição ainda
              // CarregarDadosParaEdicao será chamado pelo useEffect quando dadosInicializados = true
            } else {
              setDesligamentoAtivo(null);
              setEditandoDesligamento(false);
              LimparFormulario(); // Limpar dados se não houver desligamento ativo
            }
          } else {
            // Tentar buscar dados de desligamento especificamente
            try {
              const desligamentoResponse = await Requicicao.Get({
                endpoint: `/Funcionarios/Desligamento/${id}`,
                config: auth.GetHeaders()
              });
              
              if (desligamentoResponse.codigoEstadoHttp === 200 && desligamentoResponse.objetoResposta) {
                const desligamento = desligamentoResponse.objetoResposta;
                
                const historico = [desligamento];
                setHistorico(historico);
                
                if (desligamento.status === "ativo") {
                  setDesligamentoAtivo(desligamento);
                  setEditandoDesligamento(true); // Está editando um desligamento existente
                  setModoEdicao(false); // Mas não está em modo de edição ainda
                  // CarregarDadosParaEdicao será chamado pelo useEffect quando dadosInicializados = true
                } else {
                  setDesligamentoAtivo(null);
                  setEditandoDesligamento(false);
                  LimparFormulario();
                }
              } else {
                // Não há desligamento
                setHistorico([]);
                setDesligamentoAtivo(null);
                setEditandoDesligamento(false);
                LimparFormulario();
              }
            } catch (error) {
              console.error("❌ [GetHistorico] Erro ao buscar desligamento específico:", error);
              // Não há desligamento
              setHistorico([]);
              setDesligamentoAtivo(null);
              setEditandoDesligamento(false);
              LimparFormulario();
            }
          }
        }
      }
    } catch (error) {
      console.error("❌ [GetHistorico] Erro:", error);
    }
  };

  const AtualizarDesligamento = async (dados, arquivos) => {
    try {
      const formData = new FormData();
      
      // Dados básicos do desligamento
      formData.append('funcionarioId', dados.funcionarioId);
      formData.append('dataDesligamento', dados.dataDesligamento);
      formData.append('motivoDesligamento', dados.motivoDesligamento);
      formData.append('tipoAvisoPrevio', dados.tipoAvisoPrevio);
      formData.append('dataAvisoPrevio', dados.dataAvisoPrevio);
      formData.append('dataUltimoDia', dados.dataUltimoDia);
      formData.append('observacoes', dados.observacoes || "");
      
      // Documentos (novos ou substituição dos existentes)
      if (dados.documentos && dados.documentos.length > 0) {
        // Metadados dos documentos
        dados.documentos.forEach((doc, index) => {
          formData.append(`documentos[${index}].nomeArquivo`, doc.nomeArquivo);
          formData.append(`documentos[${index}].tipo`, doc.tipo);
        });
        
        // Arquivos reais (File objects)
        if (arquivos && arquivos.length > 0) {
          arquivos.forEach(arquivo => {
            formData.append('arquivos', arquivo);
          });
        }
      }
      
      const response = await Requicicao.Put({
        endpoint: `/Funcionarios/Desligamento/${id}`,
        data: formData,
        config: {
          ...auth.GetHeaders("only-authentication") // Sem Content-Type para multipart
        }
      });
      
      if (response.codigoEstadoHttp === 200) {
        notify({ type: "success", message: "Desligamento atualizado com sucesso!" });
        
        // Voltar para modo de visualização (não edição)
        setModoEdicao(false);
        setEditandoDesligamento(false);
        
        // Usar os dados retornados pelo PUT ao invés de fazer nova requisição
        if (response.objetoResposta) {
          const desligamentoAtualizado = response.objetoResposta;
          console.log("✅ [AtualizarDesligamento] Usando dados retornados pelo PUT:", desligamentoAtualizado);
          
          // Atualizar estados com os dados mais recentes
          setDesligamentoAtivo(desligamentoAtualizado);
          setHistorico([desligamentoAtualizado]);
          
          // Recarregar dados do formulário com os valores atualizados
          CarregarDadosParaEdicao();
        } else {
          // Fallback: recarregar dados se não houver objetoResposta
          await GetHistorico();
        }
      } else {
        notify({ type: "error", message: "Erro ao atualizar desligamento" });
      }
    } catch (error) {
      console.error("❌ [AtualizarDesligamento] Erro:", error);
      
      // Verificar se é erro de validação do backend (400)
      if (error.response?.status === 400 && error.response?.data?.mensagemAdicional) {
        notify({ type: "error", message: error.response.data.mensagemAdicional });
      } else {
        notify({ type: "error", message: "Erro ao atualizar desligamento" });
      }
    }
  };

  const CriarDesligamento = async (dados, arquivos) => {
    try {
      const formData = new FormData();
      
      // Dados básicos do desligamento
      formData.append('funcionarioId', dados.funcionarioId);
      formData.append('dataDesligamento', dados.dataDesligamento);
      formData.append('motivoDesligamento', dados.motivoDesligamento);
      formData.append('tipoAvisoPrevio', dados.tipoAvisoPrevio);
      formData.append('dataAvisoPrevio', dados.dataAvisoPrevio);
      formData.append('dataUltimoDia', dados.dataUltimoDia);
      formData.append('observacoes', dados.observacoes || "");
      
      // Metadados dos documentos
      dados.documentos.forEach((doc, index) => {
        formData.append(`documentos[${index}].nomeArquivo`, doc.nomeArquivo);
        formData.append(`documentos[${index}].tipo`, doc.tipo);
      });
      
      // Arquivos reais (File objects)
      arquivos.forEach(arquivo => {
        formData.append('arquivos', arquivo);
      });
      
      const response = await Requicicao.Post({
        endpoint: "/Funcionarios/Desligamento",
        data: formData,
        config: {
          ...auth.GetHeaders("only-authentication") // Sem Content-Type para multipart
        }
      });


      
      if (response.codigoEstadoHttp === 200) {
        notify({ type: "success", message: "Desligamento registrado com sucesso!" });
        
        // Pequeno delay para garantir que o backend processou
        await new Promise(resolve => {
          setTimeout(resolve, 500);
        });
        
        // Recarregar dados para mostrar o desligamento criado
        await GetHistorico();
      } else {
        notify({ type: "error", message: "Erro ao registrar desligamento" });
      }
    } catch (error) {
      console.error("❌ [CriarDesligamento] Erro:", error);
      
      // Verificar se é erro de validação do backend (400)
      if (error.response?.status === 400 && error.response?.data?.mensagemAdicional) {
        notify({ type: "error", message: error.response.data.mensagemAdicional });
      } else {
        notify({ type: "error", message: "Erro ao registrar desligamento" });
      }
    }
  };

  const ProcessarDesligamento = async (dados, arquivos) => {
    console.log("🚀 [ProcessarDesligamento] Iniciando função...");
    console.log("🔍 [ProcessarDesligamento] editandoDesligamento:", editandoDesligamento);
    console.log("🔍 [ProcessarDesligamento] dados:", dados);
    console.log("🔍 [ProcessarDesligamento] arquivos:", arquivos);
    
    if (editandoDesligamento) {
      console.log("🔄 [ProcessarDesligamento] Chamando AtualizarDesligamento...");
      await AtualizarDesligamento(dados, arquivos);
    } else {
      console.log("🔄 [ProcessarDesligamento] Chamando CriarDesligamento...");
      await CriarDesligamento(dados, arquivos);
    }
  };

  const ValidarCamposObrigatorios = () => {
    const erros = [];

    // Validação básica
    if (!Dados.dataDesligamento) {
      erros.push("Data do Desligamento é obrigatória");
    }

    if (!Dados.motivoDesligamento) {
      erros.push("Motivo do Desligamento é obrigatório");
    }

    if (!Dados.tipoAvisoPrevio) {
      erros.push("Tipo de Aviso Prévio é obrigatório");
    }

    if (!Dados.dataAvisoPrevio) {
      erros.push("Data do Aviso Prévio é obrigatória");
    }

    if (!Dados.dataUltimoDia) {
      erros.push("Data do Último Dia é obrigatória");
    }

    // Validação de documentos
    if (!Arquivo && !editandoDesligamento && !desligamentoAtivo) {
      erros.push("Pelo menos um documento é obrigatório");
    }

    // Se há arquivo selecionado, tipo é obrigatório
    if (Arquivo && !TipoDocumento) {
      erros.push("Tipo de documento é obrigatório");
    }

    // Se estamos editando e alterou o tipo, validar se selecionou um tipo
    if (editandoDesligamento && desligamentoAtivo?.documentos?.length > 0 && !Arquivo && !TipoDocumento) {
      erros.push("Selecione um tipo de documento para atualizar");
    }

    return erros;
  };

  const Submit = async (e) => {
    e.preventDefault();



    const erros = ValidarCamposObrigatorios();
    if (erros.length > 0) {
      notify({ type: "error", message: erros.join(", ") });
      return;
    }

    setLoading(true);

    try {
      // Preparar dados base
      const dados = {
        funcionarioId: id,
        dataDesligamento: MaskUtil.convertDateToISO(Dados.dataDesligamento),
        motivoDesligamento: Dados.motivoDesligamento,
        tipoAvisoPrevio: Dados.tipoAvisoPrevio,
        dataAvisoPrevio: MaskUtil.convertDateToISO(Dados.dataAvisoPrevio),
        dataUltimoDia: MaskUtil.convertDateToISO(Dados.dataUltimoDia),
        observacoes: Dados.observacoes || "",
        documentos: []
      };

      // Preparar documentos
      const arquivos = [];
      
      // Se há um novo arquivo selecionado
      if (Arquivo) {
        dados.documentos.push({
          nomeArquivo: Arquivo.name,
          tipo: TipoDocumento
        });
        arquivos.push(Arquivo); // Usar o File object diretamente
      }
      // Se estamos editando e há um tipo de documento selecionado (mesmo sem novo arquivo)
      else if (editandoDesligamento && TipoDocumento && desligamentoAtivo?.documentos?.length > 0) {
        // Usar o documento existente mas com novo tipo
        const documentoExistente = desligamentoAtivo.documentos[0];
        dados.documentos.push({
          nomeArquivo: documentoExistente.nomeArquivo,
          tipo: TipoDocumento // Novo tipo
        });
      }

      console.log("🔍 [Submit] Chamando ProcessarDesligamento...");
      // Decidir se criar ou atualizar
      await ProcessarDesligamento(dados, arquivos);
      console.log("🔍 [Submit] ProcessarDesligamento concluído!");
    } catch (error) {
      console.error("❌ [Submit] Erro:", error);
      notify({ type: "error", message: "Erro ao registrar desligamento" });
    } finally {
      setLoading(false);
    }
  };

  const AbrirModalCancelar = () => {
    console.log("🚀 [AbrirModalCancelar] Abrindo modal de confirmação...");
    setShowCancelarModal(true);
    console.log("🔍 [AbrirModalCancelar] Modal aberto!");
  };

  const FecharModalCancelar = () => {
    console.log("🚀 [FecharModalCancelar] Fechando modal...");
    setShowCancelarModal(false);
    console.log("🔍 [FecharModalCancelar] Modal fechado!");
  };

  const ConfirmarCancelamento = async () => {
    try {
      setLoading(true);
      
      const response = await Requicicao.Delete({
        endpoint: `/Funcionarios/Desligamento/${id}`,
        config: auth.GetHeaders()
      });

      if (response.codigoEstadoHttp === 200) {
        notify({ type: "success", message: "Desligamento cancelado com sucesso! O colaborador está ativo novamente." });
        
        // Limpar todos os estados relacionados ao desligamento
        setDesligamentoAtivo(null);
        setEditandoDesligamento(false);
        setModoEdicao(false);
        setHistorico([]);
        
        // Limpar formulário
        LimparFormulario();
        
        // Fechar modal
        FecharModalCancelar();
      } else {
        notify({ type: "error", message: "Erro ao cancelar desligamento" });
      }
    } catch (error) {
      console.error("Erro ao cancelar desligamento:", error);
      
      // Verificar se é erro de validação do backend (400)
      if (error.response?.status === 400 && error.response?.data?.mensagemAdicional) {
        notify({ type: "error", message: error.response.data.mensagemAdicional });
      } else {
        notify({ type: "error", message: "Erro ao cancelar desligamento" });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const inicializarComponente = async () => {
      if (id) {
        setLoading(true);
        try {
          // Primeiro carregar as opções
          await CarregarOpcoes();
          
          // Depois carregar o histórico
          await GetHistorico();
          
          setDadosInicializados(true);
        } catch (error) {
          console.error("❌ [useEffect] Erro ao inicializar componente:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    inicializarComponente();
  }, [id]); // Removido 'funcionario' para evitar re-execuções desnecessárias

  // useEffect para carregar dados quando desligamentoAtivo mudar e dados estiverem inicializados
  useEffect(() => {
    if (dadosInicializados && desligamentoAtivo && !modoEdicao) {
      CarregarDadosParaEdicao();
    }
  }, [dadosInicializados, desligamentoAtivo, modoEdicao]);

  const renderAlert = () => {
    if (desligamentoAtivo && !modoEdicao) {
      return (
        <div className="alert alert-danger">
          <i className="pi pi-eye me-2" />
          <strong>Funcionário Desligado:</strong> Este colaborador possui um desligamento ativo. Os dados abaixo são somente leitura. Para editar as informações do desligamento, clique no botão &quot;Editar Desligamento&quot;.
        </div>
      );
    }
    
    if (editandoDesligamento && modoEdicao) {
      return (
        <div className="alert alert-warning">
          <i className="pi pi-pencil me-2" />
          <strong>Modo de Edição:</strong> Você está editando um desligamento ativo. As alterações substituirão os dados existentes.
        </div>
      );
    }
    
    return (
      <div className="alert alert-danger">
        <i className="pi pi-exclamation-triangle me-2" />
        <strong>Atenção:</strong> Esta ação é irreversível e alterará permanentemente o status do funcionário para &quot;Desligado&quot;.
      </div>
    );
  };

  return (
    <div className="container-fluid">
      {renderAlert()}

      <form onSubmit={Submit}>
        <div className="row g-3">
          <div className="col-md-4">
            <label htmlFor="dataDesligamento" className="form-label">
              Data do Desligamento <span className="text-danger">*</span>
            </label>
            <InputDate
              id="dataDesligamento"
              name="dataDesligamento"
              value={Dados.dataDesligamento}
              onChange={(dateString) => HandleInputChange({ target: { name: "dataDesligamento", value: dateString } })}
              placeholder="dd/mm/aaaa"
              className={`w-100 ${!Dados.dataDesligamento ? 'p-invalid' : ''}`}
              disabled={desligamentoAtivo && !modoEdicao}
            />
            {!Dados.dataDesligamento && (
              <small className="text-danger">Data do Desligamento é obrigatória</small>
            )}
          </div>

          <div className="col-md-4">
            <label htmlFor="motivoDesligamento" className="form-label">
              Motivo do Desligamento <span className="text-danger">*</span>
            </label>
            <Dropdown
              id="motivoDesligamento"
              value={Dados.motivoDesligamento}
              onChange={(e) => HandleDropdownChange(e, "motivoDesligamento")}
              options={motivosDesligamento}
              className={`w-100 ${!Dados.motivoDesligamento ? 'p-invalid' : ''}`}
              optionLabel="label"
              placeholder="Selecione o motivo"
              disabled={desligamentoAtivo && !modoEdicao}
            />
            {!Dados.motivoDesligamento && (
              <small className="text-danger">Motivo do Desligamento é obrigatório</small>
            )}
          </div>

          <div className="col-md-4">
            <label htmlFor="tipoAvisoPrevio" className="form-label">
              Tipo de Aviso Prévio <span className="text-danger">*</span>
            </label>
            <Dropdown
              id="tipoAvisoPrevio"
              value={Dados.tipoAvisoPrevio}
              onChange={(e) => HandleDropdownChange(e, "tipoAvisoPrevio")}
              options={tiposAvisoPrevio}
              className={`w-100 ${!Dados.tipoAvisoPrevio ? 'p-invalid' : ''}`}
              optionLabel="label"
              placeholder="Selecione o tipo"
              disabled={desligamentoAtivo && !modoEdicao}
            />
            {!Dados.tipoAvisoPrevio && (
              <small className="text-danger">Tipo de Aviso Prévio é obrigatório</small>
            )}
          </div>

          <div className="col-md-4">
            <label htmlFor="dataAvisoPrevio" className="form-label">
              Data do Aviso Prévio <span className="text-danger">*</span>
            </label>
            <InputDate
              id="dataAvisoPrevio"
              name="dataAvisoPrevio"
              value={Dados.dataAvisoPrevio}
              onChange={(dateString) => HandleInputChange({ target: { name: "dataAvisoPrevio", value: dateString } })}
              placeholder="dd/mm/aaaa"
              className={`w-100 ${!Dados.dataAvisoPrevio ? 'p-invalid' : ''}`}
              disabled={desligamentoAtivo && !modoEdicao}
            />
            {!Dados.dataAvisoPrevio && (
              <small className="text-danger">Data do Aviso Prévio é obrigatória</small>
            )}
          </div>

          <div className="col-md-4">
            <label htmlFor="dataUltimoDia" className="form-label">
              Data do Último Dia <span className="text-danger">*</span>
            </label>
            <InputDate
              id="dataUltimoDia"
              name="dataUltimoDia"
              value={Dados.dataUltimoDia}
              onChange={(dateString) => HandleInputChange({ target: { name: "dataUltimoDia", value: dateString } })}
              placeholder="dd/mm/aaaa"
              className={`w-100 ${!Dados.dataUltimoDia ? 'p-invalid' : ''}`}
              disabled={desligamentoAtivo && !modoEdicao}
            />
            {!Dados.dataUltimoDia && (
              <small className="text-danger">Data do Último Dia é obrigatória</small>
            )}
          </div>

          <div className="col-md-12">
            <label htmlFor="observacoes" className="form-label">
              Observações
            </label>
            <InputTextarea
              id="observacoes"
              name="observacoes"
              value={Dados.observacoes}
              onChange={HandleInputChange}
              rows={3}
              className="w-100"
              placeholder="Observações adicionais sobre o desligamento..."
              disabled={desligamentoAtivo && !modoEdicao}
            />
          </div>

                     <div className="col-md-6">
             <label htmlFor="tipoDocumento" className="form-label">
               Tipo de Documento <span className="text-danger">*</span>
             </label>
             <Dropdown
               id="tipoDocumento"
               value={TipoDocumento}
               onChange={(e) => setTipoDocumento(e.value)}
               options={tiposDocumento}
               className="w-100"
               optionLabel="label"
               placeholder="Selecione o tipo"
               disabled={desligamentoAtivo && !modoEdicao}
             />
           </div>

           <div className="col-md-6">
             <label htmlFor="documentos" className="form-label">
               Documento {!editandoDesligamento && <span className="text-danger">*</span>}
             </label>
             
             {desligamentoAtivo?.documentos && desligamentoAtivo.documentos.length > 0 && (
               <div className="mb-3">
                 <small className="text-muted">Documentos existentes:</small>
                 <div className="d-flex flex-column gap-1 mt-1">
                   {desligamentoAtivo.documentos.map((doc, index) => (
                     <Button
                       key={doc.id || index}
                       className="btn btn-sm btn-outline-info"
                       type="button"
                       icon="pi pi-download"
                       onClick={(e) => {
                         e.preventDefault();
                         e.stopPropagation();
                         HandleDownloadDocumento(doc.id, doc.nomeArquivo);
                       }}
                       tooltip={`Baixar ${doc.nomeArquivo}`}
                       label={doc.nomeArquivo}
                       disabled={!doc.id}
                     />
                   ))}
                 </div>
                 <small className="text-info">Novos documentos substituirão os existentes. Você também pode alterar apenas o tipo do documento existente.</small>
               </div>
             )}
             
             <FileUpload
               chooseLabel="Escolher"
               uploadLabel="Enviar"
               cancelLabel="Cancelar"
               accept="application/pdf"
               emptyTemplate={
                 <p className="m-0">
                   Arraste e solte os arquivos aqui para fazer upload.
                 </p>
               }
               customUpload
               onSelect={HandleFileSelect}
               className={!Arquivo && !editandoDesligamento ? 'p-invalid' : ''}
               disabled={desligamentoAtivo && !modoEdicao}
             />
             {!Arquivo && !editandoDesligamento && (
               <small className="text-danger">Pelo menos um documento é obrigatório</small>
             )}
           </div>

        </div>
      </form>

      {/* Botões de Ação */}
      <div className="col-md-12 mt-3">
        <div className="d-flex flex-row-reverse gap-3">
          {/* Botão principal */}
          {(() => {
            if (desligamentoAtivo && !modoEdicao) {
              return (
                <Button
                  className="btn btn-warning"
                  type="button"
                  label="Editar Desligamento"
                  icon="pi pi-pencil"
                  onClick={HabilitarEdicao}
                />
              );
            }
            
            if (editandoDesligamento && modoEdicao) {
              return (
                <Button
                  className="btn btn-warning"
                  type="button"
                  label="Atualizar Desligamento"
                  icon="pi pi-save"
                  onClick={(e) => {
                    e.preventDefault();
                    Submit(e);
                  }}
                  disabled={!Dados.dataDesligamento || !Dados.motivoDesligamento || !Dados.tipoAvisoPrevio || !Dados.dataAvisoPrevio || !Dados.dataUltimoDia || (Arquivo && !TipoDocumento)}
                />
              );
            }
            
            return (
              <Button
                className="btn btn-danger"
                type="button"
                label="Confirmar Desligamento"
                icon="pi pi-user-minus"
                onClick={(e) => {
                  e.preventDefault();
                  Submit(e);
                }}
                disabled={!Dados.dataDesligamento || !Dados.motivoDesligamento || !Dados.tipoAvisoPrevio || !Dados.dataAvisoPrevio || !Dados.dataUltimoDia || (!Arquivo && !editandoDesligamento && !desligamentoAtivo) || !TipoDocumento}
              />
            );
          })()}

          {/* Botão Cancelar Edição */}
          {editandoDesligamento && modoEdicao && (
            <Button
              className="btn btn-outline-danger"
              type="button"
              label="Cancelar Edição"
              icon="pi pi-times"
              onClick={CancelarEdicao}
            />
          )}

          {/* Botão Cancelar Desligamento */}
          {desligamentoAtivo && (
            <Button
              className="btn btn-outline-danger"
              type="button"
              label="Cancelar Desligamento"
              icon="pi pi-times"
              onClick={AbrirModalCancelar}
            />
          )}

          {/* Botão Limpar Campos */}
          {!desligamentoAtivo && (
            <Button
              className="btn btn-outline-secondary"
              type="button"
              label="Limpar Campos"
              icon="pi pi-refresh"
              onClick={() => {
                setDados({
                  dataDesligamento: "",
                  motivoDesligamento: "",
                  tipoAvisoPrevio: "",
                  dataAvisoPrevio: "",
                  dataUltimoDia: "",
                  observacoes: "",
                  documentos: []
                });
                setArquivo(null);
                setTipoDocumento(null);
              }}
            />
          )}
        </div>
      </div>

      {/* Modal de Confirmação para Cancelar Desligamento */}
      <Dialog
        visible={showCancelarModal}
        onHide={FecharModalCancelar}
        header="⚠️ Confirmar Cancelamento de Desligamento"
        modal
        className="p-fluid"
        style={{ width: '500px' }}
        footer={
          <div className="d-flex justify-content-end gap-2">
            <Button
              label="Cancelar"
              icon="pi pi-times"
              className="btn btn-outline-secondary"
              onClick={FecharModalCancelar}
            />
            <Button
              label="Sim, Cancelar Desligamento"
              icon="pi pi-check"
              className="btn btn-danger"
              onClick={ConfirmarCancelamento}
            />
          </div>
        }
      >
        <div className="p-4">
          <div className="alert alert-warning mb-3">
            <i className="pi pi-exclamation-triangle me-2" />
            <strong>Atenção!</strong> Esta ação é irreversível.
          </div>
          
          <h5 className="text-danger mb-3">
            <i className="pi pi-user me-2" />
            Cancelar Desligamento
          </h5>
          
          <p className="mb-3">
            Você está prestes a <strong>cancelar o desligamento</strong> deste colaborador. 
            Esta ação terá as seguintes consequências:
          </p>
          
          <ul className="list-unstyled mb-4">
            <li className="mb-2">
              <i className="pi pi-check-circle text-success me-2" />
              <strong>Colaborador será reativado</strong> - O status voltará para &quot;Ativo&quot;
            </li>
            <li className="mb-2">
              <i className="pi pi-exclamation-triangle text-warning me-2" />
              <strong>Histórico será perdido</strong> - Todos os dados do desligamento serão removidos permanentemente
            </li>
            <li className="mb-2">
              <i className="pi pi-exclamation-triangle text-warning me-2" />
              <strong>Documentos serão excluídos</strong> - Todos os arquivos anexados ao desligamento serão perdidos
            </li>
            <li className="mb-2">
              <i className="pi pi-info-circle text-info me-2" />
              <strong>Não será possível desfazer</strong> - Esta ação não pode ser revertida
            </li>
          </ul>
          
          <div className="alert alert-info">
            <i className="pi pi-info-circle me-2" />
            <strong>Confirmação necessária:</strong> Para prosseguir, clique em &quot;Sim, Cancelar Desligamento&quot;.
          </div>
        </div>
      </Dialog>

      <ConfirmPopup />
    </div>
  );
};

export default Desligamento; 